import { CommonModule, DatePipe } from '@angular/common'; // Import CommonModule
import { Component, Input, OnInit, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DateTime } from 'luxon';

import { StaticOptions } from '../../core/constants';
import { ScheduledNotificationListDto, ScheduledNotificationsClient } from '../../core/services/api.service';
import { RequestService } from '../../core/services/request.service';
import { NotificationAddEditModalComponent } from '../notification-add-edit-modal/notification-add-edit-modal.component';

@Component({
  selector: 'sn-user-notifications',
  templateUrl: './user-notifications.component.html',
  styleUrl: './user-notifications.component.scss',
  imports: [CommonModule, DatePipe] // Add CommonModule and DatePipe here
})
export class UserNotificationComponent implements OnInit {
  private _requestService = inject(RequestService);
  private _scheduledNotificationsClient = inject(ScheduledNotificationsClient);
  protected _matDialog = inject(MatDialog);

  @Input() email: string | undefined;
  @Input() showAdd: boolean = true;

  scheduledNotifications: ScheduledNotificationListDto[] = [];
  notificationTypes = StaticOptions.notificationTypes;
  isInit: boolean = true;

  ngOnInit(): void {
    this.getUserScheduledNotifications();
  }

  getNotificationName(key: string) {
    return this.notificationTypes.find(x => x.Id == key)?.Name;
  }

  showScheduledNotificationModal(id?: number | undefined) {
    const dialogRef = this._matDialog.open(NotificationAddEditModalComponent, {
      width: '500px',
      data: {
        id: id,
        userEmail: this.email
      }
    });

    dialogRef.afterClosed().subscribe((result: boolean) => {
      if (result) {
        this.getUserScheduledNotifications();
      }
    });
  }

  getUserScheduledNotifications() {
    this._requestService.get(
      this._scheduledNotificationsClient.scheduledNotifications_GetUserScheduledNotificationList(this.email),
      (x) => {
        this.scheduledNotifications = x;
        this.isInit = false;
      },
      'Scheduled Notifications'
    );
  }

  toLocalTimeDisplay(displayTimeUtc: string | undefined) {
    if (displayTimeUtc == undefined) {
      return ''; // Return empty string for undefined input
    }

    try {
      const parts = displayTimeUtc.split(/[: ]/);
      if (parts.length < 3) {
        console.error('Invalid displayTimeUtc format:', displayTimeUtc);
        return ''; // Handle potential format errors
      }

      const hourStr = parts[0];
      const minuteStr = parts[1];
      const period = parts[2];

      const hour24 = this.to24HourFormat(hourStr, period);
      const minute = parseInt(minuteStr, 10);

      if (isNaN(minute) || isNaN(parseInt(hour24, 10))) {
        console.error('Could not parse time:', displayTimeUtc);
        return ''; // Handle parsing errors
      }

      let date = DateTime.utc().set({ hour: parseInt(hour24, 10), minute: minute, second: 0 });
      let userDisplayTime = date.setZone(DateTime.local().zoneName).toFormat('t');

      // Luxon's 't' format might not always include leading zero for hours < 10 AM.
      // Manually format if needed, although 't' usually handles localization well.
      // Example: Ensure 'h:mm a' format if 't' isn't sufficient
      // userDisplayTime = date.setZone(DateTime.local().zoneName).toFormat('h:mm a');

      return userDisplayTime;
    } catch (error) {
      console.error('Error formatting time:', error);
      return ''; // Return empty string on error
    }
  }

  private to24HourFormat(hour: string, period: string): string {
    let hourNum = parseInt(hour, 10);
    if (isNaN(hourNum)) return '00'; // Handle parse error

    const lowerPeriod = period.toLowerCase();

    if (lowerPeriod === 'am') {
      if (hourNum === 12) { // Midnight case
        hourNum = 0;
      }
    } else if (lowerPeriod === 'pm') {
      if (hourNum !== 12) { // Afternoon/evening case, noon is 12
        hourNum += 12;
      }
    }

    // Format to two digits
    return hourNum.toString().padStart(2, '0');
  }

  countNotificationEmails(str: string): number {
    return str.split(',').map(email => email.trim()).filter(email => email.includes('@')).length;
  }
}
